import {BRAND_STROKES, BRAND_VIEWBOX} from './ink-brand.js';

const clamp = value => Math.max(0, Math.min(1, value));
const ease = value => {const p = clamp(value); return p * p * (3 - 2 * p);};
const hash = (x, y, seed) => {
  let value = Math.imul(x ^ seed * 193, 374761393) + Math.imul(y, 668265263);
  value = Math.imul(value ^ value >>> 13, 1274126177); return ((value ^ value >>> 16) >>> 0) / 4294967295;
};

function schedule() {
  let cursor = 0;
  const result = BRAND_STROKES.map((stroke, index) => {
    const pause = index === 5 ? .23 : index ? .035 + (index % 3) * .014 : 0;
    cursor += pause;
    const duration = .11 + .36 * Math.sqrt(stroke.length / 583.53);
    const item = {...stroke, start: cursor, duration}; cursor += duration * .92; return item;
  });
  const ratio = 1.38 / cursor;
  result.forEach(stroke => {stroke.start = .12 + stroke.start * ratio; stroke.duration *= ratio;});
  return result;
}

// Bake a time-of-deposition field into the original ink silhouette. Each texel
// records WHEN the travelling brush touched it, how much pressure was present,
// and a directionally aligned dry-bristle value. There is no SVG dash animation
// and no per-frame rasterization: seeking samples this immutable paint field.
function bakeInk(THREE) {
  const width = 1536, height = Math.round(width * BRAND_VIEWBOX[3] / BRAND_VIEWBOX[2]);
  const scale = width / BRAND_VIEWBOX[2], count = width * height;
  const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
  const context = canvas.getContext('2d', {willReadFrequently: true});
  if (!context) throw new Error('The calligraphy surface requires a 2D canvas.');
  const birth = new Float32Array(count).fill(2), flow = new Float32Array(count), fibre = new Float32Array(count);
  const coverage = new Uint8Array(count), strokes = schedule();
  let inkPixels = 0;
  for (let index = 0; index < strokes.length; index++) {
    const stroke = strokes[index], path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', stroke.trace);
    const length = path.getTotalLength(), samples = Math.max(12, Math.ceil(length / 2.4));
    const points = Array.from({length: samples + 1}, (_, step) => {
      const u = step / samples, p = path.getPointAtLength(u * length);
      const before = path.getPointAtLength(Math.max(0, u * length - 1)), after = path.getPointAtLength(Math.min(length, u * length + 1));
      const dx = after.x - before.x, dy = after.y - before.y, magnitude = Math.hypot(dx, dy) || 1;
      return {u, x: p.x * scale, y: p.y * scale, nx: -dy / magnitude, ny: dx / magnitude};
    });
    const radius = Math.ceil(stroke.width * scale * .98);
    const x0 = Math.max(0, Math.floor(Math.min(...points.map(p => p.x)) - radius));
    const y0 = Math.max(0, Math.floor(Math.min(...points.map(p => p.y)) - radius));
    const x1 = Math.min(width, Math.ceil(Math.max(...points.map(p => p.x)) + radius));
    const y1 = Math.min(height, Math.ceil(Math.max(...points.map(p => p.y)) + radius));
    const bw = x1 - x0, bh = y1 - y0;
    context.setTransform(1, 0, 0, 1, 0, 0); context.clearRect(0, 0, width, height);
    context.setTransform(scale, 0, 0, scale, 0, 0); context.fillStyle = '#fff'; context.fill(new Path2D(stroke.outline));
    const mask = context.getImageData(x0, y0, bw, bh).data;
    const distance = new Float32Array(bw * bh).fill(Infinity), nearest = new Uint16Array(bw * bh);
    for (let sample = 0; sample < points.length; sample++) {
      const p = points[sample];
      const left = Math.max(x0, Math.floor(p.x - radius)), right = Math.min(x1 - 1, Math.ceil(p.x + radius));
      const top = Math.max(y0, Math.floor(p.y - radius)), bottom = Math.min(y1 - 1, Math.ceil(p.y + radius));
      for (let y = top; y <= bottom; y++) for (let x = left; x <= right; x++) {
        const local = (y - y0) * bw + x - x0;
        if (!mask[local * 4 + 3]) continue;
        const squared = (x + .5 - p.x) ** 2 + (y + .5 - p.y) ** 2;
        if (squared < distance[local]) {distance[local] = squared; nearest[local] = sample;}
      }
    }
    for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) {
      const local = (y - y0) * bw + x - x0, alpha = mask[local * 4 + 3];
      if (!alpha) continue;
      const p = points[nearest[local]], u = Number.isFinite(distance[local]) ? p.u : 1;
      // A small load, fluent travel and a final lift; unequal stroke lengths
      // retain unequal durations and a longer breath separates the characters.
      const travel = .17 * (1 - (1 - u) ** 3) + .67 * u + .16 * u ** 5;
      const pressure = .38 + .62 * Math.pow(Math.sin(Math.PI * u), .65);
      const born = stroke.start + stroke.duration * travel;
      const global = y * width + x;
      if (born < birth[global]) {
        birth[global] = born;
        const cross = ((x - p.x) * p.nx + (y - p.y) * p.ny) / scale;
        flow[global] = .38 + .62 * pressure * (1 - .48 * clamp(Math.sqrt(distance[local]) / (stroke.width * scale * .55)));
        // Long grain follows the actual brush direction, rather than an evenly
        // applied noise filter. The brand's original filled edges are preserved.
        fibre[global] = clamp(.60 + .24 * Math.sin(cross * 1.0 + index * 1.37 + Math.sin(u * 8.5) * .35) + .09 * hash(x >> 2, y >> 2, index + 9));
      }
      coverage[global] = Math.max(coverage[global], alpha);
    }
  }
  const data = new Uint8Array(count * 4);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const i = y * width + x, destination = ((height - 1 - y) * width + x) * 4;
    if (!coverage[i]) continue;
    inkPixels++;
    // Premultiply all fields by coverage. Linear/mipmap filtering can then be
    // unpremultiplied in the shader without inventing early birth times around
    // unwritten edges (which would incorrectly preview a hollow outline).
    data[destination] = Math.round(clamp(birth[i] / 1.7) * coverage[i]);
    data[destination + 1] = Math.round(clamp(flow[i]) * coverage[i]);
    data[destination + 2] = Math.round(clamp(fibre[i]) * coverage[i]);
    data[destination + 3] = coverage[i];
  }
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.UnsignedByteType);
  texture.colorSpace = THREE.NoColorSpace; texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter; texture.generateMipmaps = true; texture.needsUpdate = true;
  return {texture, metadata: {source: 'V23 original calligraphy: 20 unchanged filled paths', width, height, inkPixels, technique: 'pressure / deposition-time / directional bristle field on a displaced Three.js surface'}};
}

export function createBrandInk({THREE, scene}) {
  const {texture, metadata} = bakeInk(THREE);
  const uniforms = {
    uInk: {value: texture}, uClock: {value: 0}, uUnfold: {value: 0}, uOpacity: {value: 1},
    uColor: {value: new THREE.Color('#dfceb0')}, uTexel: {value: new THREE.Vector2(1 / metadata.width, 1 / metadata.height)}
  };
  const material = new THREE.ShaderMaterial({
    uniforms, transparent: true, depthWrite: false, side: THREE.DoubleSide,
    vertexShader: `varying vec2 vUv;uniform sampler2D uInk;uniform float uUnfold;void main(){vUv=uv;vec3 p=position;vec4 ink=texture2D(uInk,uv);p.z+=ink.g*.014;p.z-=uUnfold*pow(1.-uv.x,2.)*2.1;gl_Position=projectionMatrix*modelViewMatrix*vec4(p,1.);}`,
    fragmentShader: `
      uniform sampler2D uInk;uniform float uClock,uUnfold,uOpacity;uniform vec3 uColor;uniform vec2 uTexel;varying vec2 vUv;
      void main(){
        vec4 raw=texture2D(uInk,vUv);vec4 ink=vec4(raw.rgb/max(raw.a,.003),raw.a);float born=ink.r*1.7;
        float write=smoothstep(born-.008,born+.026,uClock);
        float lift=smoothstep(.0,.035,uClock-born)*(1.-smoothstep(.04,.16,uClock-born));
        float fibre=.58+.42*ink.b;
        float deposition=ink.a*write*(.88+.12*ink.g);
        float erase=smoothstep((1.-vUv.x)*.30+ink.b*.34,(1.-vUv.x)*.30+ink.b*.34+.32,uUnfold);
        float relief=(texture2D(uInk,vUv+vec2(-uTexel.x,uTexel.y)*1.4).a-ink.a)*.065;
        vec3 color=uColor*(.68+.32*ink.g)*fibre+vec3(.07,.058,.036)*lift*ink.g+relief;
        gl_FragColor=vec4(color,deposition*(1.-erase)*uOpacity);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 390 / 840, 100, 48), material);
  mesh.position.set(.1, 2.25, 14.2); mesh.renderOrder = 12; mesh.frustumCulled = false; scene.add(mesh);
  let width = 1;
  return {
    mesh, metadata,
    setWidth(value) {width = value; mesh.scale.setScalar(width);},
    point(x, y) {return new THREE.Vector3(.1 + (x / 840 - .5) * width, 2.25 + (.5 - y / 390) * width * 390 / 840, 14.2);},
    update(time, unfold, opacity = 1) {
      uniforms.uClock.value = time; uniforms.uUnfold.value = unfold; uniforms.uOpacity.value = opacity;
      mesh.visible = opacity > .001 && unfold < 1;
    },
    dispose() {scene.remove(mesh); mesh.geometry.dispose(); material.dispose(); texture.dispose();}
  };
}
